import React, { useState } from 'react';
import SubscriptionSideComponent from '../../components/SubscriptionSideComponent/SubscriptionSideComponent';
import Container from '../../components/Container';
import Card from '../../components/Card';
import PhoneInput from '../../components/fields/PhoneInput';
import Main from '../../components/Main';
import messages from '../BankDetails/messages';
import loginMessages from '../Login/messages';
import compensationMessages from '../CompensationCheck/messages';
import phoneMessages from '../../views/PersonalInfo/messages';
import { useIntl } from 'react-intl';
import Input from '../../components/fields/Input';
import { Form, Formik } from 'formik';
import * as yup from 'yup';
import '../../utils/yupIntlSetup';
import {
  validateFirstname,
  validateLastname,
  validatePhoneNumber,
} from '../../utils/yupValidators';
import {
  jsonApiAxios,
  clearAllCookies,
  getResponseErrors,
} from '../../services/api';
import useAuthenticate from '../../hooks/useAuthenticate';
import Alert from '../../components/Alert';
import { createCustomer } from '../../services/resources/customers';
import { useCoBrandingParams } from '../../hooks/useCobrandingParams';

const PurchasePremium = () => {
  const [currentPage, setCurrentPage] = useState('login');
  const [errors, setErrors] = useState<null | string[]>(null);
  const { dynamicConfig } = useCoBrandingParams();
  const country = dynamicConfig?.country?.toUpperCase();
  const locale = dynamicConfig?.locale;
  const { doLogin } = useAuthenticate();
  const { formatMessage } = useIntl();

  const loginValidationSchema = yup.object().shape({
    password: yup.string().required(),
    email: yup
      .string()
      .email()
      .required(),
  });

  const signupValidationSchema = yup.object().shape({
    email: yup
      .string()
      .email()
      .required(),
    password: yup
      .string()
      .min(8)
      .required(),
    firstname: validateFirstname,
    lastname: validateLastname,
    phone: validatePhoneNumber.required(),
  });

  const loginInitialValues = {
    email: '',
    password: '',
  };

  const signupInitialValues = {
    email: '',
    password: '',
    firstname: '',
    lastname: '',
    phone: '',
    address: '',
  };

  const openStripe = async () => {
    const stripeUrl = await jsonApiAxios.post(
      '/subscriptionCheckout',
      {},
      {
        schema: {},
        type: 'subscriptionCheckout',
      }
    );
    if (stripeUrl?.data?.data?.attributes?.url) {
      window.location.href = stripeUrl?.data?.data?.attributes?.url;
    }
  };

  const login = async (values: any, formikHelpers: any) => {
    try {
      const response = await jsonApiAxios.post('/login', values, {
        noSerialize: true,
      });
      if (response) {
        doLogin();
        openStripe();
      }
    } catch (error) {
      /**
       * Login failure
       */
      clearAllCookies();
      formikHelpers.setSubmitting(false);
      if (error?.response) {
        setErrors(getResponseErrors(error));
      }
    }
  };

  const handleSubmit = async (values: any, formikHelpers: any) => {
    if (currentPage === 'login') {
      await login(values, formikHelpers);
    } else {
      try {
        const { id: customerId } = await createCustomer({
          ...values,
        });
        if (customerId) {
          localStorage.setItem('yo:customerId', customerId);
          doLogin();
          openStripe();
        }
      } catch (error) {
        setCurrentPage('login');
        formikHelpers.setFieldValue('password', '');
        formikHelpers.setFieldTouched('email', true);
        setErrors(getResponseErrors(error));
      }
    }
  };

  const handlePageSwitch = () => {
    setCurrentPage(currentPage === 'signup' ? 'login' : 'signup');
  };

  return (
    <Container>
      <SubscriptionSideComponent />
      <Main>
        <Card>
          <div className="flex flex-col" style={{ gap: '26px' }}>
            <div className="flex flex-col" style={{ gap: '6px' }}>
              <span style={headerStyles}>
                {formatMessage(messages.premiunSubscriptionTitle)}
              </span>
              <span style={subHeaderStyles}>
                {formatMessage(messages.premiunSubscriptionSubTitle)}
              </span>
            </div>
            <div>
              {currentPage === 'login' ? (
                <span style={labelStyles}>
                  {formatMessage(messages.logInAccountLabel)}
                </span>
              ) : (
                <span style={labelStyles}>
                  {formatMessage(messages.createAccountLabel)}
                </span>
              )}
            </div>
            <div>
              {errors && errors.length > 0
                ? errors.map((error, index) => (
                    <Alert
                      // eslint-disable-next-line
                      key={`error-${index}`}
                      level="danger"
                      className="w-full my-20"
                    >
                      {error}
                    </Alert>
                  ))
                : null}
            </div>
            {currentPage === 'signup' ? (
              <Formik
                initialValues={signupInitialValues}
                validationSchema={signupValidationSchema}
                onSubmit={handleSubmit}
                validateOnChange={true}
                validateOnBlur={true}
              >
                {({ isSubmitting, isValid }) => (
                  <Form>
                    <div className="flex flex-col">
                      <div className="fieldset sm:flex-row">
                        <Input
                          name="firstname"
                          label={phoneMessages.firstName}
                          placeholder={phoneMessages.enterFirstNamePlaceholder}
                          autoComplete="given-name"
                        />
                        <Input
                          name="lastname"
                          label={phoneMessages.lastName}
                          placeholder={phoneMessages.enterLastNamePlaceholder}
                          autoComplete="family-name"
                        />
                      </div>
                      <div className="fieldset sm:flex-row">
                        <Input
                          name="email"
                          label={formatMessage(
                            compensationMessages.yourEmailAddress
                          )}
                          placeholder={formatMessage(
                            loginMessages.placeholderEmail
                          )}
                          autoComplete="email"
                        />
                        <Input
                          name="password"
                          type="password"
                          autoComplete="password"
                          label={formatMessage(loginMessages.labelPassword)}
                          placeholder={formatMessage(
                            loginMessages.placeholderPassword
                          )}
                        />
                      </div>
                      <div className="fieldset sm:flex-row">
                        <PhoneInput
                          name="phone"
                          label={phoneMessages.phone}
                          placeholder={phoneMessages.phonePlaceholder}
                          portal={country}
                          locale={locale}
                        />
                        <div style={{ width: '50%' }} />
                      </div>
                      <div
                        className="flex flex-col items-center"
                        style={{ gap: '12px', marginTop: '16px' }}
                      >
                        <div className="flex flex-col" style={{ gap: '14px' }}>
                          <button
                            className="bank-details-button-confirmation"
                            type="submit"
                            disabled={!isValid || isSubmitting}
                            style={{ width: '232px' }}
                          >
                            {formatMessage(loginMessages.signUpButton)}
                          </button>
                          <span
                            onClick={handlePageSwitch}
                            style={{ textAlign: 'center' }}
                          >
                            {formatMessage(messages.haveAccountLabel, {
                              // eslint-disable-next-line react/no-multi-comp
                              bold: (...chunks) => (
                                <a
                                  rel="noreferrer"
                                  style={{ marginLeft: '3px' }}
                                >
                                  {chunks}
                                </a>
                              ),
                            })}
                          </span>
                        </div>
                        <span>
                          {formatMessage(messages.permissionLabel, {
                            // eslint-disable-next-line react/no-multi-comp
                            tac: (...chunks) => (
                              <a
                                className="link"
                                target="_blank"
                                rel="noreferrer"
                                href={dynamicConfig?.termsOfServiceUrl}
                              >
                                {chunks}
                              </a>
                            ),
                          })}
                        </span>
                      </div>
                    </div>
                  </Form>
                )}
              </Formik>
            ) : (
              <Formik
                initialValues={loginInitialValues}
                validationSchema={loginValidationSchema}
                onSubmit={handleSubmit}
                validateOnMount={true}
              >
                {({ isSubmitting, isValid }) => {
                  return (
                    <Form>
                      <div className="flex flex-col" style={{ gap: '32px' }}>
                        <div className="fieldset sm:flex-row">
                          <Input
                            name="email"
                            label={formatMessage(
                              compensationMessages.yourEmailAddress
                            )}
                            placeholder={formatMessage(
                              loginMessages.placeholderEmail
                            )}
                            autoComplete="email"
                          />
                          <Input
                            name="password"
                            type="password"
                            autoComplete="password"
                            label={formatMessage(loginMessages.labelPassword)}
                            placeholder={formatMessage(
                              loginMessages.placeholderPassword
                            )}
                          />
                        </div>
                        <div
                          className="flex flex-col items-center"
                          style={{ gap: '12px' }}
                        >
                          <div
                            className="flex flex-col"
                            style={{ gap: '14px' }}
                          >
                            <button
                              className="bank-details-button-confirmation"
                              type="submit"
                              disabled={!isValid || isSubmitting}
                              style={{ width: '232px' }}
                            >
                              {formatMessage(loginMessages.loginButton)}
                            </button>
                            <span
                              onClick={handlePageSwitch}
                              style={{ textAlign: 'center' }}
                            >
                              {formatMessage(messages.accontLabel, {
                                // eslint-disable-next-line react/no-multi-comp
                                bold: (...chunks) => (
                                  <a
                                    rel="noreferrer"
                                    style={{ marginLeft: '3px' }}
                                  >
                                    {chunks}
                                  </a>
                                ),
                              })}
                            </span>
                          </div>
                          <span>
                            {formatMessage(messages.permissionLabel, {
                              // eslint-disable-next-line react/no-multi-comp
                              tac: (...chunks) => (
                                <a
                                  className="link"
                                  target="_blank"
                                  rel="noreferrer"
                                  href={dynamicConfig?.termsOfServiceUrl}
                                >
                                  {chunks}
                                </a>
                              ),
                            })}
                          </span>
                        </div>
                      </div>
                    </Form>
                  );
                }}
              </Formik>
            )}
          </div>
        </Card>
      </Main>
    </Container>
  );
};

export default PurchasePremium;

const headerStyles = {
  fontWeight: 600,
  fontSize: '30px',
  letterSpacing: '-2%',
  color: '#101828',
};

const subHeaderStyles = {
  color: '#667085',
  fontWeight: 400,
  fontSize: '16px',
};

const labelStyles = {
  fontWeight: 600,
  fontSize: '20px',
  letterSpacing: '-2%',
  color: '#101828',
};
